import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ExternalLink, ArrowRight, Bell } from 'lucide-react';
import { Button, Input, StatusBadge } from '../../components/common';
import { formatCurrencyPrice } from '../../utils/currency';
import { apiClient } from '../../api/client';
import type { VerifyExtractionResult } from './VerificationProgress';

export interface ProductPreviewCardProps {
  url: string;
  jobId: string;
  preview: VerifyExtractionResult;
  onWatchCreated: () => void;
  onCancel: () => void;
}

export const ProductPreviewCard: React.FC<ProductPreviewCardProps> = ({
  url,
  jobId,
  preview,
  onWatchCreated,
  onCancel,
}) => {
  const [targetPrice, setTargetPrice] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const currency = preview.currency ?? null;
  const price = preview.price ?? null;
  const listPrice = preview.list_price;

  let domain = url;
  try {
    domain = new URL(url).hostname;
  } catch {}

  const isTrackable = preview.trackability_status === 'TRACKABLE';

  const createWatchMutation = useMutation({
    mutationFn: async () => {
      setErrorMsg(null);
      // 1. Create watch
      const { data, error } = await apiClient.POST('/api/v1/watches', {
        body: {
          url: preview.normalized_url || url,
          verify_job_id: jobId,
        },
      });

      if (error) {
        throw error;
      }

      // 2. If target price set, upsert alert rule
      const trimmedTarget = targetPrice.trim();
      if (trimmedTarget && !isNaN(Number(trimmedTarget)) && Number(trimmedTarget) > 0 && data?.id) {
        try {
          await apiClient.PUT('/api/v1/watches/{id}/alert', {
            params: { path: { id: data.id } },
            body: {
              target_price: trimmedTarget,
              target_currency: currency,
              notify_on_any_drop: false,
              min_drop_pct: 3.0,
              min_drop_abs: 1.0,
              cooldown_seconds: 600,
              is_active: true,
            },
          });
        } catch {
          // If alert rule fails, watch is still created
        }
      }

      return data;
    },
    onSuccess: () => {
      onWatchCreated();
    },
    onError: (err: any) => {
      setErrorMsg(err.message || 'Failed to create price watch.');
    },
  });

  return (
    <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 shadow-sm space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {domain}
            </span>
            {preview.storefront_context?.country_code && (
              <span
                data-testid="preview-country-badge"
                className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
              >
                {preview.storefront_context.country_code}
              </span>
            )}
            {preview.trackability_status === 'GEO_MISMATCH' ? (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60">
                HELD
              </span>
            ) : (
              <StatusBadge status={preview.trackability_status} />
            )}
            {preview.availability && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {preview.availability}
              </span>
            )}
          </div>
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 line-clamp-2">
            {preview.title || 'Extracted Product'}
          </h3>
        </div>

        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
          title="Open product page"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Extracted Price presentation */}
      <div className="flex items-baseline gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800">
        <span className="text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono tracking-tight">
          {formatCurrencyPrice(price, currency)}
        </span>
        {listPrice && price && parseFloat(listPrice) > parseFloat(price) && (
          <span className="text-sm text-slate-400 dark:text-slate-500 line-through">
            List: {formatCurrencyPrice(listPrice, currency)}
          </span>
        )}
      </div>

      {/* Optional Target Price threshold */}
      {isTrackable && (
        <div className="space-y-1.5">
          <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300">
            <Bell className="w-3.5 h-3.5 text-indigo-500" />
            <span>Target Price Alert (Optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              step="0.01"
              placeholder={price ? `e.g. ${(parseFloat(price) * 0.9).toFixed(2)}` : 'e.g. 99.00'}
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              className="max-w-xs"
            />
            {currency && <span className="text-xs font-semibold text-slate-500">{currency}</span>}
          </div>
          <p className="text-[11px] text-slate-400">
            Leave blank to receive alerts on any significant detected price drop.
          </p>
        </div>
      )}

      {errorMsg && (
        <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs">
          {errorMsg}
        </div>
      )}

      {/* Card Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={() => createWatchMutation.mutate()}
          isLoading={createWatchMutation.isPending}
          disabled={!isTrackable}
        >
          <span>Start Tracking</span>
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
